import { reduce } from 'lodash';
import moment from 'moment';
import theme from 'theme';
import i18n from 'i18n-js';
import Store from 'state/store';
import { hexToRGB, formatDate } from 'utils';

// Set reservation styles according to status
const reservationStyles = (status) => {
  let styles = {};

  switch (status) {
    case 'PENDING':
      styles = {
        background: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
      };
      break;
    case 'NOSHOW':
      styles = {
        background: theme.palette.grey[500],
        border: `1px solid theme.palette.grey[500]`,
      };
      break;
    case 'OFFLINE':
      styles = {
        color: '#000000',
        background: '#ffffff',
        backgroundSize: '11px 11px',
        backgroundImage: `linear-gradient(45deg, ${theme.palette.grey[300]} 25%, transparent 25%, transparent 50%, ${theme.palette.grey[300]} 50%, ${theme.palette.grey[300]} 75%, transparent 75%, #fff)`,
        border: `1px solid ${theme.palette.grey[400]}`,
      };
      break;
    case 'COMPLETED':
      styles = {
        background: theme.palette.grey[500],
        border: `1px solid ${theme.palette.grey[500]}`,
      };
      break;
    case 'RESERVED':
      styles = {
        background: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
      };
      break;
    case 'RESERVED_LATE':
      styles = {
        background: theme.palette.warning.main,
        border: `1px solid ${theme.palette.warning.main}`,
      };
      break;
    case 'INPROGRESS':
      styles = {
        background: theme.palette.success.main,
        border: `1px solid ${theme.palette.success.main}`,
      };
      break;
    case 'INPROGRESS_LATE':
      styles = {
        background: theme.palette.error.main,
        border: `1px solid ${theme.palette.error.main}`,
      };
      break;
    default:
      break;
  }
  styles.zIndex = 100;
  return styles;
};

const bufferStyles = (styles) => {
  const color = styles?.background || theme.palette.grey[300];
  return {
    ...styles,
    zIndex: 99,
    border: 'none',
    borderStyle: 'none',
    background: `repeating-linear-gradient(135deg, ${hexToRGB(color, 0.15)}, ${hexToRGB(
      color,
      0.15
    )} 4px, ${hexToRGB(color, 0.4)} 4px, ${hexToRGB(color, 0.4)} 5px)`,
  };
};

// Get reservation status and return styles
export const getReservationStyles = (data) => {
  if (data.is_buffer) {
    data.start_used = data.reservation.start_used || data.reservation.start_reserved;
    data.end_used = data.reservation.end_used || data.reservation.end_reserved;
  }
  let late = false;
  if (data.status === 'RESERVED' && moment(data.start_used) < moment()) {
    late = true;
  } else if (data.status === 'INPROGRESS' && moment(data.end_used) < moment()) {
    late = true;
  }

  const status = late ? data.status + '_LATE' : data.status;
  return reservationStyles(status);
};

export const getStatusesStyles = () => {
  return {
    color: '#000000',
    background: '#ffffff',
    backgroundSize: '11px 11px',
    backgroundImage: `linear-gradient(45deg, ${theme.palette.grey[300]} 25%, transparent 25%, transparent 50%, ${theme.palette.grey[300]} 50%, ${theme.palette.grey[300]} 75%, transparent 75%, #fff)`,
    border: `1px solid ${theme.palette.grey[400]}`,
    zIndex: 99,
  };
};

// Get reservation information
export const getReservationInformation = (reservation, currentTz) => {
  const state = Store.getState();
  const { localeFormat } = state?.organizations || {};
  const fullName = reservation?.user?.first_name
    ? `${reservation?.user?.last_name}, ${reservation?.user?.first_name}`
    : reservation?.service?.name
      ? `${reservation?.service?.name}`
      : '';
  const info = {
    status: reservation.status || '',
    reservation_id: reservation.id || '',
    username: reservation?.user?.name || fullName,
    start_date: reservation.start_used
      ? moment(reservation.start_used).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
    end_date: reservation.end_used
      ? moment(reservation.end_used).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
    end_buffer: reservation.end_used
      ? moment(reservation.end_buffer).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
  };
  const infoOffline = {
    reason: reservation.offline_reason || '',
    description: reservation.description || '',
    start_date: reservation.start_used
      ? moment(reservation.start_used).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
    end_date: reservation.end_used
      ? moment(reservation.end_used).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
    end_buffer: reservation.end_used
      ? moment(reservation.end_buffer).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
  };

  const statusData = {
    description: reservation.description || '',
    start_date: reservation.start_time
      ? moment(reservation.start_time).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
    end_date: reservation.end_time
      ? moment(reservation.end_time).tz(currentTz).format(`${localeFormat} h:mm a`)
      : '',
  };

  return reservation.v2s
    ? i18n.t('reservations.reservation_status', statusData)
    : reservation.status !== 'OFFLINE'
      ? i18n.t('reservations.reservation_info', info)
      : i18n.t('reservations.reservation_info_offline', infoOffline);
};

// Get buffer information
export const getBufferInformation = (buffer) => {
  const state = Store.getState();
  const { localeFormat } = state?.organizations || {};
  const info = {
    reservation_id: buffer.id,
    username: buffer.user.name,
    start_buffer: moment(buffer.start_used).format(`${localeFormat} h:mm a`),
    end_buffer: buffer.end_used ? moment(buffer.end_buffer).format(`${localeFormat} h:mm a`) : '',
  };
  return i18n.t('dashboard.buffer_info', info);
};

// `currentTz` is accepted but never forwarded — the timezone is re-guessed below. Pre-existing
// behaviour, preserved deliberately; see the AGI-64017 task spec follow-ups.
// eslint-disable-next-line no-unused-vars
export const formatUsageData = (data, currentTz = moment.tz.guess()) => {
  const rer = sessionStorage.getItem('reservation_engine');
  if (rer === 'V2') return formatUsageDataV2(data, moment.tz.guess());
  return formatUsageDataV1(data, moment.tz.guess());
};

export const formatUsageDataV1 = (data, currentTz = moment.tz.guess()) => {
  const formattedData = reduce(
    data,
    (result, group) => {
      result.groups.push({
        id: group.id,
        key: group.id,
        name:
          group.display_name ||
          (group.license_plate ? `${group.name} - ${group.license_plate}` : group.name),
        vehicleGroup: group.vehicle_group,
        vehicleType: group.vehicle_type,
        location: group?.vehicle_group?.primary_location || '',
      });
      const buffers = [];
      const reservations = group?.reservations?.map((reservation) => {
        const reservationToAdd = {
          canMove: false,
          canResize: false,
          groupId: group.id,
          id: reservation.id,
          key: reservation.id,
          status: reservation.status,
          name: reservation.user.name,
          reservationType: reservation.reservation_type,
          canAccess: reservation.can_access,
          endReserved: reservation.end_reserved,
          styles: getReservationStyles(reservation),
          vehicleStatus: reservation.vehicle_status,
          startUsed: formatDate(moment(reservation.start_used), currentTz),
          endBuffer: moment(reservation.end_buffer || null).tz(currentTz),
          startReserved: reservation.start_reserved,
          subscriptionId: reservation.subscription_id,
          information: getReservationInformation(reservation, currentTz),
          description: reservation.description ? reservation.description : undefined,
          offlineReason: reservation.offline_reason ? reservation.offline_reason : undefined,
          endUsed: formatDate(
            reservation.end_used ? moment(reservation.end_used) : moment().add(1000, 'years'),
            currentTz
          ),
        };
        if (reservationToAdd?.endBuffer && moment(reservationToAdd?.endBuffer).isValid()) {
          // eslint-disable-next-line no-unused-vars -- `information` is dropped on purpose
          const { information, ...reservationData } = reservationToAdd;
          buffers.push({
            ...reservationData,
            type: 'BUFFER',
            id: `buffer-${reservationToAdd?.id}`,
            key: `buffer-${reservationToAdd?.id}`,
            startUsed: reservationToAdd.startUsed,
            endUsed: reservationToAdd.endBuffer,
            styles: bufferStyles(reservationToAdd?.styles),
          });
        }
        return reservationToAdd;
      });
      result.items.push(...reservations);
      result.items.push(...buffers);
      return result;
    },
    { groups: [], items: [] }
  );
  return formattedData;
};

// Parse usage data
export const formatUsageDataV2 = (data, currentTz = moment.tz.guess()) => {
  const formattedData = reduce(
    data,
    (result, group) => {
      result.groups.push({
        key: `${group.vehicle_group.id}-${group.vehicle.id}`,
        id: group.vehicle.id,
        name: group?.vehicle?.license_plate
          ? `${group?.vehicle?.name} - ${group?.vehicle?.license_plate}`
          : group?.vehicle?.name,
        vehicleGroup: group.vehicle_group,
        vehicleType: group.vehicle.vehicle_type,
        location: group?.location || '',
      });
      const usageTimes = manageUsages(group, currentTz);
      const statusesTimes = manageStatuses(group, currentTz);
      result.items.push(...usageTimes, ...statusesTimes);
      return result;
    },
    { groups: [], items: [] }
  );
  return formattedData;
};

const manageStatuses = (group, currentTz) => {
  return group?.vehicle_to_statuses?.map((vehicleStatus) => {
    let usageTime = {
      canMove: false,
      canResize: false,
      groupId: `${group.vehicle_group.id}-${group.vehicle.id}`,
      name: vehicleStatus.description,
      id: '',
      key: `${group?.vehicle?.id}-${vehicleStatus.id}`,
      status: vehicleStatus.status,
      styles: getStatusesStyles(vehicleStatus),
      vehicleStatus: vehicleStatus.status,
      startUsed: formatDate(moment(vehicleStatus.start_time), currentTz),
      description: vehicleStatus?.description,
      offlineReason: vehicleStatus?.offline_reason,
      information: getReservationInformation({ ...vehicleStatus, v2s: true }, currentTz),
      endUsed: formatDate(
        vehicleStatus.end_time ? moment(vehicleStatus.end_time) : moment().add(1000, 'years'),
        currentTz
      ),
    };

    return usageTime;
  });
};

const getUsageStatus = (vehicleUsage, reservation) => {
  return vehicleUsage.status == 'PENDING' ||
    vehicleUsage.status === 'CANCELLED' ||
    !vehicleUsage.status
    ? reservation.status
    : vehicleUsage.status;
};

const manageUsages = (group, currentTz) => {
  return group?.vehicle_usages?.map((vehicleUsage) => {
    const { reservation = {} } = vehicleUsage;
    const usageStatus = getUsageStatus(vehicleUsage, reservation);
    let usageTime = {
      canMove: false,
      canResize: false,
      canAccess: reservation?.user?.first_name ? true : false,
      groupId: `${group.vehicle_group.id}-${group.vehicle.id}`,
      key: `${group?.vehicle?.id}-${reservation.id}`,
      id: reservation.id,
      status: usageStatus,
      name:
        reservation?.user?.name || reservation?.user?.first_name
          ? `${reservation?.user?.last_name}, ${reservation?.user?.first_name}`
          : reservation?.service?.name || '',
      endReserved: reservation.end_reserved,
      styles: getReservationStyles({
        ...vehicleUsage,
        status: usageStatus,
      }),
      vehicleStatus: getVehicleStatus(group.vehicle_to_statuses, group?.vehicle?.id),
      startUsed: formatDate(moment(vehicleUsage.start_used), currentTz),
      startReserved: reservation.start_reserved,
      subscriptionId: reservation.subscription_id,
      information: getReservationInformation(
        {
          ...reservation,
          status: usageStatus,
          end_buffer: getEndBuffer(reservation.id, group.vehicle_usages),
          end_used: vehicleUsage.end_used,
        },
        currentTz
      ),
      description: reservation?.description,
      offlineReason: reservation?.offline_reason,
      endUsed: formatDate(
        vehicleUsage.end_used ? moment(vehicleUsage.end_used) : moment().add(1000, 'years'),
        currentTz
      ),
    };
    if (vehicleUsage.is_buffer) {
      const bufferData = {
        ...usageTime,
        information: null,
        type: 'BUFFER',
        id: `buffer-${usageTime?.id}`,
        key: `buffer-${usageTime?.key}`,
        styles: bufferStyles(usageTime.styles),
      };
      usageTime = bufferData;
    }
    return usageTime;
  });
};

const getEndBuffer = (reservationId, usages) => {
  const buffer = usages.find((usage) => usage.is_buffer && usage.reservation.id == reservationId);
  return buffer?.end_used;
};

const getVehicleStatus = (v2s, vehicleId) => {
  const v2sByVehicle = v2s.find((vehicleStatus) => vehicleStatus.vehicle_id == vehicleId);
  if (!v2sByVehicle) return 'ACTIVE';
  return v2sByVehicle.status || '';
};

// get reservations from vehicle usages
export const getAllReservationsFromUsages = (data = []) => {
  let reservations = [];
  data.map((items) => {
    if (items?.reservations) {
      return items?.reservations?.map((item) =>
        reservations.push({
          ...item,
          key: item.id,
          id: item.id,
          vehicle: {
            id: items.id,
            name: items.name,
          },
          vehicleGroup: items.vehicle_group,
          time_zone: items?.vehicle_group?.time_zone || moment.tz.guess(),
        })
      );
    }

    if (items?.vehicle_usages) {
      items?.vehicle_usages?.map((vehicle_usage) => {
        reservations.push({
          ...vehicle_usage.reservation,
          key: `${items?.vehicle?.id}-${vehicle_usage.reservation.id}`,
          id: vehicle_usage.reservation.id,
          vehicle: {
            id: items?.vehicle?.id,
            name: items?.vehicle?.name,
          },
          vehicleGroup: items.vehicle_group,
          time_zone: items?.vehicle_group?.time_zone || moment.tz.guess(),
        });
      });
    }
  });
  return reservations;
};
